// @ts-nocheck
/**
 * Lazy-loaded API client for better performance and bundle size optimization
 * Only loads API classes when they are actually needed
 */

import type { 
  Configuration,
  AuthApi,
  PaymentsApi,
  MachinesApi,
  StoreApi,
  HuggingfaceApi,
  LocalAIProxyApi,
  RequestsApi,
} from '../generated/api';

/**
 * Type definitions for API constructors
 */
type ApiConstructor<T> = new (configuration: Configuration) => T;

// Lazy import functions for API classes
const lazyImports = {
  AuthApi: () => import('../generated/api').then(m => m.AuthApi),
  PaymentsApi: () => import('../generated/api').then(m => m.PaymentsApi),
  MachinesApi: () => import('../generated/api').then(m => m.MachinesApi),
  StoreApi: () => import('../generated/api').then(m => m.StoreApi),
  HuggingfaceApi: () => import('../generated/api').then(m => m.HuggingfaceApi),
  LocalAIProxyApi: () => import('../generated/api').then(m => m.LocalAIProxyApi),
  RequestsApi: () => import('../generated/api').then(m => m.RequestsApi),
  Configuration: () => import('../generated/api').then(m => m.Configuration),
};

// Cache for loaded API instances
const apiCache = new Map<string, unknown>();

/**
 * Lazy API client that loads API classes on demand
 */
export class LazyApiClient {
  private configuration: Configuration | null = null;

  /**
   * Get or create configuration instance
   */
  private async getConfiguration(): Promise<Configuration> {
    if (!this.configuration) {
      const ConfigurationClass = await lazyImports.Configuration();
      const baseURL = process.env.REACT_APP_API_URL || (
        process.env.NODE_ENV === 'development' ? '' : 'https://api.swarmind.ai'
      );
      
      this.configuration = new ConfigurationClass({
        basePath: baseURL,
        baseOptions: {
          withCredentials: true,
          timeout: 30000,
          headers: {
            'Content-Type': 'application/json',
          },
        },
        accessToken: () => {
          return localStorage.getItem('bearerToken') || '';
        },
      });
    }
    return this.configuration;
  }

  /**
   * Get or create API instance with caching
   */
  private async getApiInstance<T>(apiName: keyof typeof lazyImports): Promise<T> {
    const cacheKey = apiName;
    
    if (apiCache.has(cacheKey)) {
      return apiCache.get(cacheKey) as T;
    }

    const [ApiClass, configuration] = await Promise.all([
      lazyImports[apiName](),
      this.getConfiguration(),
    ]);

    const instance = new (ApiClass as ApiConstructor<T>)(configuration);
    apiCache.set(cacheKey, instance);
    
    return instance;
  }

  /**
   * Lazy-loaded Auth API
   */
  async getAuthApi(): Promise<AuthApi> {
    return this.getApiInstance<AuthApi>('AuthApi');
  }

  /**
   * Lazy-loaded Payments API
   */
  async getPaymentsApi(): Promise<PaymentsApi> {
    return this.getApiInstance<PaymentsApi>('PaymentsApi');
  }

  /**
   * Lazy-loaded Machines API
   */
  async getMachinesApi(): Promise<MachinesApi> {
    return this.getApiInstance<MachinesApi>('MachinesApi');
  }

  /**
   * Lazy-loaded Store API
   */
  async getStoreApi(): Promise<StoreApi> {
    return this.getApiInstance<StoreApi>('StoreApi');
  }

  /**
   * Lazy-loaded Huggingface API
   */
  async getHuggingfaceApi(): Promise<HuggingfaceApi> {
    return this.getApiInstance<HuggingfaceApi>('HuggingfaceApi');
  }

  /**
   * Lazy-loaded LocalAI Proxy API
   */
  async getLocalAIProxyApi(): Promise<LocalAIProxyApi> {
    return this.getApiInstance<LocalAIProxyApi>('LocalAIProxyApi');
  }

  /**
   * Lazy-loaded Requests API
   */
  async getRequestsApi(): Promise<RequestsApi> {
    return this.getApiInstance<RequestsApi>('RequestsApi');
  }

  /**
   * Clear API cache (useful for testing or configuration changes)
   */
  clearCache(): void {
    apiCache.clear();
    this.configuration = null;
  }

  /**
   * Preload specific API classes for better UX
   * Call this for APIs that will be used soon
   */
  async preloadApis(apiNames: (keyof typeof lazyImports)[]): Promise<void> {
    const promises = apiNames.map(apiName => this.getApiInstance(apiName));
    await Promise.all(promises);
  }

  /**
   * Get cache statistics for monitoring
   */
  getCacheStats(): { size: number; keys: string[] } {
    return {
      size: apiCache.size,
      keys: Array.from(apiCache.keys()),
    };
  }
}

// Export singleton instance
export const lazyApiClient = new LazyApiClient();
export default lazyApiClient;